import React, { useEffect, useRef, useState } from "react";
import "./Analytics.css";

// In-page quick-nav (lives inside the fixed Analytics header) for the single-scroll page. Highlights
// whichever section is currently in view via IntersectionObserver rather than
// tracking scrollTop manually.
const SectionNav = ({ sections, topOffset = 96, onNavigate }) => {
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const observerRef = useRef(null);
  const navRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          const top = visible.reduce((a, b) => (a.boundingClientRect.top < b.boundingClientRect.top ? a : b));
          setActiveId(top.target.id);
        }
      },
      { rootMargin: `-${topOffset}px 0px -70% 0px`, threshold: 0 }
    );

    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [sections, topOffset]);

  // The strip scrolls horizontally on narrow screens; keep the active pill in view.
  useEffect(() => {
    const nav = navRef.current;
    const link = nav?.querySelector(".section-nav-link-active");
    if (!nav || !link) return;
    nav.scrollTo({ left: link.offsetLeft - (nav.clientWidth - link.offsetWidth) / 2, behavior: "smooth" });
  }, [activeId]);

  const handleClick = (id) => (e) => {
    e.preventDefault();
    onNavigate?.();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav ref={navRef} className="section-nav" aria-label="Dashboard sections">
      {sections.map((s) => (
        <a
          key={s.id}
          href={`#${s.id}`}
          className={`section-nav-link${activeId === s.id ? " section-nav-link-active" : ""}`}
          onClick={handleClick(s.id)}
        >
          {s.label}
        </a>
      ))}
    </nav>
  );
};

export default SectionNav;
